/**
 * Traduction de l'enveloppe d'aperçu du serveur vers le contrat `BulkImpact`.
 *
 * Le serveur ne renvoie JAMAIS de phrase : il renvoie un code de motif stable
 * (`ALREADY_DELETED`, `WORKFLOW_NOT_FOUND`…). Une phrase serait intraduisible et
 * figerait le français. C'est donc ici qu'un code devient une clé i18n, préfixée
 * par la liste appelante — chaque liste parle avec SON mot métier
 * (« documents », « workflows », « exécutions », « personnes »).
 *
 * ⛔ **Une liste ne réécrit pas cette conversion chez elle.** Une copie en ligne
 * n'a l'air de rien — trois lignes de `map` — mais c'est un second endroit où le
 * `?? []` défensif, le `toLowerCase()` ou le champ `truncated` peuvent diverger,
 * et la divergence ne se voit qu'à l'écran d'un client. Toutes les listes
 * passent par ici.
 *
 * Convention : `.agent/conventions/bulk-action-impact-preview.md`.
 */

import type { BulkImpact } from '@/components/ui/data-table/actions/action-types'

/** Ce que rend `POST …?dry_run=true` (schéma `BulkPreviewResponse` côté serveur). */
export interface BulkImpactPayload {
    will_apply: number
    skipped: Array<{ reason: string; count: number; sample_names?: string[] }>
    total: number
    truncated?: boolean
}

/**
 * @param payload  réponse brute de la route de masse en `dry_run`
 * @param reasonKeyPrefix  espace de noms i18n des motifs de CETTE liste,
 *   **sans point final** — le mappeur l'ajoute : `'ged.bulk.reason'` →
 *   `ALREADY_DELETED` devient `ged.bulk.reason.already_deleted`. Un point en
 *   trop produit `ged.bulk.reason..already_deleted`, une clé introuvable qui
 *   s'affiche en « motif non précisé » sans rien casser ailleurs.
 */
export function toBulkImpact(payload: BulkImpactPayload, reasonKeyPrefix: string): BulkImpact {
    return {
        willApply: payload.will_apply,
        total: payload.total,
        truncated: payload.truncated,
        skipped: (payload.skipped ?? []).map((reason) => ({
            reasonKey: `${reasonKeyPrefix}.${reason.reason.toLowerCase()}`,
            count: reason.count,
            sampleNames: reason.sample_names,
        })),
    }
}
